import { DamageDepositModeId, FullBooking, Offer, pluralize } from '@alluradirect/react';
import { BookingCheckoutResponse } from 'components/views/pages/Checkout/hooks/useCheckout';
import { PaymentTypeEnum } from 'components/views/pages/Checkout/types';
import { formatCanadianCurrency } from 'helpers/currencyHelpers';

interface Args {
  checkoutData: BookingCheckoutResponse | undefined;
  bookingData: FullBooking | undefined;
  isLastStep: boolean;
}

export function serviceFeeNet({ checkoutData, bookingData, isLastStep }: Args): number {
  if (isLastStep) {
    return bookingData ? Number(bookingData.SERVICE_FEE.FEE) : 0;
  }
  return checkoutData ? Number(checkoutData.BOOKING.SERVICE_FEE.FEE) : 0;
}

export function propertyManagerServiceFeeNet({ checkoutData, bookingData, isLastStep }: Args): number {
  if (isLastStep) {
    return bookingData ? Number(bookingData.PROPERTY_MANAGER_SERVICE_FEE.NET) : 0;
  }
  return checkoutData ? Number(checkoutData.BOOKING.PROPERTY_MANAGER_SERVICE_FEE.NET) : 0;
}

export function surchargesNet({ checkoutData, bookingData, isLastStep }: Args): number {
  if (isLastStep) {
    return bookingData ? Number(bookingData.SURCHARGES.NET) : 0;
  }
  return checkoutData ? Number(checkoutData.BOOKING.SURCHARGES.NET) : 0;
}

// TODO: fees and surcharges. what goes here, is this correct?
export function feesAndSurchargesTotal(args: Args): number {
  const serviceFee = serviceFeeNet(args);
  const propertyManagerServiceFee = propertyManagerServiceFeeNet(args);
  const surcharges = surchargesNet(args);

  const total = serviceFee + propertyManagerServiceFee + surcharges;
  return total;
}

export function nightsTotal(args: Args): number {
  if (args.isLastStep) {
    return args.bookingData?.NIGHTS ?? 0;
  }
  return args.checkoutData?.BOOKING?.NIGHTS ?? 0;
}

export function pricePerNightTotal(args: Args): number {
  if (args.isLastStep) {
    return args.bookingData ? Number(args.bookingData.PAYMENT.PRICE_PER_NIGHT) : 0;
  }
  return args.checkoutData ? Number(args.checkoutData.BOOKING.PAYMENT.PRICE_PER_NIGHT) : 0;
}

export function nightlyRatesBreakDown(args: Args): any[] {
  if (args.isLastStep) {
    return args.bookingData?.NIGHTLY_RATES ?? [];
  }
  return args.checkoutData ? args.checkoutData.BOOKING.NIGHTLY_RATES : [];
}

export function nightlyRatesDisplay(args: Args): string {
  const nights = nightsTotal(args);
  const pricePerNight = accommodationSubtotalBaseRate(args)/nights;
  return `${formatCanadianCurrency(pricePerNight)} x ${nights} ${pluralize(nights, 'night')}`;
}

export function accommodationSubtotalBaseRate(args: Args): number {
  if (args.isLastStep) {
    args.bookingData ? Number(args.checkoutData?.BOOKING.PAYMENT_INFORMATION.BOOKING_NET_BASE_RATE) : 0;
  }
  return args.checkoutData ? Number(args.checkoutData.BOOKING?.PAYMENT_INFORMATION.BOOKING_NET_BASE_RATE) : 0;
}

export function bookingTotal(args: Args): number {
  if (args.isLastStep) {
    return args.bookingData ? Number(args.bookingData.TOTAL) : 0;
  }
  return args.checkoutData ? Number(args.checkoutData.BOOKING.TOTAL) : 0;
}

export function lengthOfStayDiscount(args: Args): number {
  if (args.isLastStep) {
    return args.bookingData ? Number(args.bookingData.PAYMENT_INFORMATION.LENGTH_OF_STAY_DISCOUNT) : 0;
  }
  return args.checkoutData ? Number(args.checkoutData.BOOKING?.PAYMENT_INFORMATION.LENGTH_OF_STAY_DISCOUNT) : 0;
}

export function ownerOfferDiscount(args: Args): number {
  if (args.isLastStep) {
    return args.bookingData ? Number(args.bookingData.PAYMENT_INFORMATION.OWNER_OFFER_DISCOUNT) : 0;
  }
  return args.checkoutData ? Number(args.checkoutData.BOOKING.PAYMENT_INFORMATION.OWNER_OFFER_DISCOUNT) : 0;
}

export function alluraOfferDiscount(args: Args): number {
  if (args.isLastStep) {
    return args.bookingData ? Number(args.bookingData.PAYMENT_INFORMATION.ALLURA_OFFER_DISCOUNT) : 0;
  }
  return args.checkoutData ? Number(args.checkoutData.BOOKING.PAYMENT_INFORMATION.ALLURA_OFFER_DISCOUNT) : 0;
}

export function getOffersData(args: Args): { ownerOffer: Offer, alluraOffer: Offer, ownerGiftCertificate: Offer, alluraGiftCertificate: Offer } {
  if (args.isLastStep) {
    return  {
      ownerOffer: args.bookingData?.OFFERS.OWNER.OFFER || undefined,
      alluraOffer: args.bookingData?.OFFERS.ALLURA.OFFER || undefined,
      ownerGiftCertificate: args.bookingData?.GIFT_CERTIFICATES.OWNER.OFFER || undefined,
      alluraGiftCertificate: args.bookingData?.GIFT_CERTIFICATES.ALLURA.OFFER || undefined,
    };
  }
  return {
    ownerOffer: args.checkoutData?.BOOKING.OFFERS.OWNER.OFFER || undefined,
    alluraOffer: args.checkoutData?.BOOKING.OFFERS.ALLURA.OFFER || undefined,
    ownerGiftCertificate: args.checkoutData?.BOOKING.GIFT_CERTIFICATES.OWNER.OFFER || undefined,
    alluraGiftCertificate: args.checkoutData?.BOOKING.GIFT_CERTIFICATES.ALLURA.OFFER || undefined,
};
}

export function hasLengthOfStayDiscount(args: Args): boolean {
  const losDiscount = lengthOfStayDiscount(args);
  return losDiscount > 0;
}

export function taxTotal(args: Args): number {
  if (args.isLastStep) {
    return args.bookingData ? Number(args.bookingData.TAX.BOOKING_AND_FEES_TOTAL) : 0;
  }
  return args.checkoutData ? Number(args.checkoutData.BOOKING.TAX.BOOKING_AND_FEES_TOTAL) : 0;
}

export function taxTotalsBreakdown(args: Args): any[] {
  if (args.isLastStep) {
    return args.bookingData?.TAX.BOOKING_AND_FEES ?? [];
  }
  return args.checkoutData ? args.checkoutData.BOOKING.TAX.BOOKING_AND_FEES : [];
}

export function hasOwnerGiftCertificate(args: Args): boolean {
  if (args.isLastStep) {
    return args.bookingData && args.bookingData.GIFT_CERTIFICATES.OWNER.OFFER ? true : false;
  }
  return args.checkoutData && args.checkoutData.BOOKING.GIFT_CERTIFICATES.OWNER.OFFER ? true : false;
}

export function getOwnerGiftCertificateValue(args: Args): string {
  if (args.isLastStep) {
    return args.bookingData && args.bookingData.GIFT_CERTIFICATES.OWNER.OFFER ? args.bookingData.GIFT_CERTIFICATES.OWNER.OFFER.TITLE : '';
  }
  return args.checkoutData && args.checkoutData.BOOKING.GIFT_CERTIFICATES.OWNER.OFFER ? args.checkoutData.BOOKING.GIFT_CERTIFICATES.OWNER.OFFER.TITLE : '';
}

export function hasAlluraGiftCertificate(args: Args): boolean {
  if (args.isLastStep) {
    return args.bookingData && args.bookingData.GIFT_CERTIFICATES.ALLURA.OFFER ? true : false;
  }
  return args.checkoutData && args.checkoutData.BOOKING.GIFT_CERTIFICATES.ALLURA.OFFER ? true : false;
}

export function getAlluraGiftCertificateValue(args: Args): string {
  if (args.isLastStep) {
    return args.bookingData && args.bookingData.GIFT_CERTIFICATES.ALLURA.OFFER ? args.bookingData.GIFT_CERTIFICATES.ALLURA.OFFER.TITLE : '';
  }
  return args.checkoutData && args.checkoutData.BOOKING.GIFT_CERTIFICATES.ALLURA.OFFER ? args.checkoutData.BOOKING.GIFT_CERTIFICATES.ALLURA.OFFER.TITLE : '';
}


// TODO: verify the correctness of this code
export function damageDepositAmount(args: Args): number {
  const { checkoutData, bookingData, isLastStep } = args;
  let mode = undefined;
  let amount = 0;

  if (isLastStep) {
    mode = bookingData ? bookingData.PAYMENT.DAMAGE_DEPOSIT.MODE_ID : undefined;
    amount = bookingData ? Number(bookingData.PAYMENT.DAMAGE_DEPOSIT.AMOUNT) : 0;
  } else {
    mode = checkoutData ? checkoutData.BOOKING.PAYMENT.DAMAGE_DEPOSIT.MODE_ID : undefined;
    amount = checkoutData ? Number(checkoutData.BOOKING.PAYMENT.DAMAGE_DEPOSIT.AMOUNT) : 0;
  }

  if (
    !mode ||
    mode === DamageDepositModeId.COLLECT_AT_CHECKIN ||
    mode === DamageDepositModeId.COLLECT_DAMAGE_UPON_DEPARTURE
  ) {
    return 0;
  }

  return amount;
}

export function damageDepositDescription(args: Args): string {
  const { checkoutData, isLastStep, bookingData } = args;

  if (isLastStep) {
    return bookingData?.PROPERTY.DAMAGE_DEPOSIT.DESCRIPTION ?? '';
  }
  return checkoutData?.BOOKING.PROPERTY.DAMAGE_DEPOSIT.DESCRIPTION ?? '';
}

interface HasBalancePaymentArgs {
  paymentType: string | undefined;
  checkoutData: BookingCheckoutResponse | undefined;
}

export function hasBalancePayment({ paymentType, checkoutData }: HasBalancePaymentArgs): boolean {
  return paymentType === PaymentTypeEnum.DepositBalanceDue.toString() && Boolean(checkoutData);
}

export function balancePaymentTotal(checkoutData: BookingCheckoutResponse | undefined): number {
  return checkoutData ? Number(checkoutData.BOOKING.PAYMENT.BALANCE.TOTAL) : 0;
}

export function balancePaymentDate(checkoutData: BookingCheckoutResponse | undefined): string {
  return checkoutData?.BOOKING.PAYMENT.BALANCE.DUE_DATE ?? '';
}
